import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";

/**
 * Full-width DESKTOP-ONLY banner shown on the home page between the
 * ticker and the "LAteST from LAST" heading/icons row — the same slot
 * as `HomeStrip`, which is the mobile-only counterpart. Admin manages
 * slides (image + link URL) and picks either a single fixed slide or
 * auto-rotate through all of them in Admin → Homepage strip. Renders
 * nothing when disabled or empty.
 */
export default function HomepageBanner() {
  const [config, setConfig] = useState(null);
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api
      .get("/settings/homepage-banner")
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, []);

  const slides = (config?.slides || []).filter((s) => s && s.image_url);
  const isRotate = config?.display_mode !== "single";

  useEffect(() => {
    if (!isRotate || slides.length < 2) return undefined;
    timerRef.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, config?.interval_ms || 5000);
    return () => window.clearInterval(timerRef.current);
  }, [isRotate, config?.interval_ms, slides.length]);

  if (!config?.enabled || slides.length === 0) return null;

  // Single mode: show the admin-picked slide; fall back to the first
  // slide if the picked one was since removed, so the banner never
  // just vanishes because of a stale id.
  let slide;
  if (isRotate) {
    slide = slides[index % slides.length];
  } else {
    slide = slides.find((s) => s.id === config.selected_slide_id) || slides[0];
  }

  const go = (delta) => {
    setIndex((i) => (i + delta + slides.length) % slides.length);
  };

  return (
    <section
      data-testid="home-banner"
      className="relative mx-auto hidden max-w-7xl px-8 pt-0 sm:block"
    >
      <div className="relative overflow-hidden rounded-md border border-black/10">
        <a
          href={slide.link_url || "#"}
          target={slide.open_in_new_tab !== false ? "_blank" : "_self"}
          rel="noreferrer"
          data-testid="home-banner-slide-link"
          className="block"
          onClick={() =>
            trackEvent("homepage_banner_click", { display_mode: config.display_mode })
          }
        >
          <img
            src={slide.image_url}
            alt={slide.alt || ""}
            data-testid="home-banner-image"
            className="aspect-[2000/409] w-full object-cover"
          />
        </a>

        {isRotate && slides.length > 1 ? (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous banner"
              data-testid="home-banner-prev"
              className="absolute left-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60"
            >
              <ChevronLeft size={16} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next banner"
              data-testid="home-banner-next"
              className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60"
            >
              <ChevronRight size={16} strokeWidth={2.5} />
            </button>
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {slides.map((s, i) => (
                <button
                  key={s.id || i}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Go to banner ${i + 1}`}
                  data-testid={`home-banner-dot-${i}`}
                  className={`h-1.5 w-1.5 rounded-full transition-colors ${
                    i === index % slides.length ? "bg-white" : "bg-white/40"
                  }`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
